<script setup lang="ts">
import { computed } from 'vue'

import BaseImage from '@/components/atoms/BaseImage.vue'
import BetaPageOrderHandle from '@/components/beta-dashboard/atoms/BetaPageOrderHandle.vue'
import BetaPagePublicLink from '@/components/beta-dashboard/atoms/BetaPagePublicLink.vue'
import BetaPageSettingsIcon from '@/components/beta-dashboard/atoms/BetaPageSettingsIcon.vue'
import type { BetaPage, BetaPagePreview } from '@/types/beta-dashboard'

const props = defineProps<{
    page: BetaPage
    preview: BetaPagePreview
    postCount?: number
    locked?: boolean
    orderDisabled?: boolean
    orderDisabledReason?: string
}>()

const emit = defineEmits<{
    open: [page: BetaPage]
    configure: [page: BetaPage]
}>()

const publicationCount = computed(() => (
    props.preview.kind === 'list'
        ? `${props.postCount ?? 0} ${props.postCount === 1 ? 'publicação' : 'publicações'}`
        : null
))

const presentation = computed(() => ({
    home: {
        label: 'Home',
        icon: '⌂',
    },
    list: {
        label: props.page.options?.galleryType === 'isotope' ? 'Portfólio' : 'Listagem',
        icon: '▦',
    },
    link: {
        label: 'Link externo',
        icon: '↗',
    },
    content: {
        label: 'Página',
        icon: 'Aa',
    },
}[props.preview.kind]))
</script>

<template>
    <article class="beta-page-card">
        <BetaPageOrderHandle
            class="beta-page-card__order"
            :name="page.name"
            :locked="locked || page.isHome"
            :disabled="orderDisabled"
            :disabled-reason="orderDisabledReason"
        />
        <button
            type="button"
            class="beta-page-card__open"
            :aria-label="`Abrir ${page.name}`"
            :title="page.name"
            @click="emit('open', page)"
        >
            <span :class="['beta-page-card__preview', `is-${preview.kind}`]">
                <BaseImage
                    v-if="preview.image"
                    :src="preview.image"
                    :alt="`Prévia de ${page.name}`"
                    class-name="beta-page-card__image"
                />
                <span v-else class="beta-page-card__fallback" aria-hidden="true">
                    <span>{{ presentation.icon }}</span>
                    <small>{{ presentation.label }}</small>
                </span>

                <span class="beta-page-card__type">{{ presentation.label }}</span>
                <span v-if="page.visible === 0" class="beta-page-card__visibility">Oculta</span>
            </span>

            <span class="beta-page-card__body">
                <span>
                    <strong>{{ page.name }}</strong>
                    <small v-if="publicationCount">{{ publicationCount }}</small>
                </span>
            </span>
        </button>
        <div class="beta-page-card__actions">
            <BetaPagePublicLink :page="page" />
            <button
                type="button"
                class="beta-page-card__settings"
                :aria-label="`Configurar ${page.name}`"
                :title="`Configurar ${page.name}`"
                @click="emit('configure', page)"
            >
                <BetaPageSettingsIcon aria-hidden="true" />
            </button>
        </div>
    </article>
</template>

<style scoped>
.beta-page-card {
    position: relative;
    display: grid;
    min-width: 0;
    overflow: hidden;
    border: 1px solid #dfe6ee;
    border-radius: 22px;
    background: #fff;
    transition: border-color 170ms ease, transform 170ms ease;
}

.beta-page-card:hover,
.beta-page-card:focus-within {
    border-color: color-mix(in srgb, var(--main-color) 34%, #dfe6ee);
    transform: translateY(-2px);
}

.beta-page-card:focus-within {
    outline: 3px solid color-mix(in srgb, var(--main-color) 18%, transparent);
    outline-offset: 2px;
}

.beta-page-card__open {
    display: grid;
    min-width: 0;
    text-align: left;
}

.beta-page-card__preview {
    position: relative;
    display: block;
    height: 148px;
    overflow: hidden;
    background: #edf2f7;
}

.beta-page-card__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 220ms ease;
}

.beta-page-card:hover .beta-page-card__image {
    transform: scale(1.025);
}

.beta-page-card__fallback {
    display: grid;
    width: 100%;
    height: 100%;
    place-content: center;
    justify-items: center;
    gap: 6px;
    color: #798ba4;
}

.beta-page-card__preview.is-home .beta-page-card__fallback {
    background:
        radial-gradient(circle at 78% 18%, color-mix(in srgb, var(--main-color) 18%, transparent), transparent 34%),
        linear-gradient(145deg, #f8fbff, #e8f0fa);
}

.beta-page-card__preview.is-list .beta-page-card__fallback {
    background:
        linear-gradient(135deg, rgba(255, 255, 255, 0.7), transparent),
        repeating-linear-gradient(90deg, #e9edf5 0 31%, #f6f8fb 31% 34%);
}

.beta-page-card__preview.is-link .beta-page-card__fallback {
    background: linear-gradient(145deg, #f7f4ff, #ebe6f7);
}

.beta-page-card__preview.is-content .beta-page-card__fallback {
    background:
        linear-gradient(#dce4ef 0 0) 20px 24px / 62% 7px no-repeat,
        linear-gradient(#e6ebf2 0 0) 20px 42px / 78% 5px no-repeat,
        linear-gradient(#e6ebf2 0 0) 20px 54px / 68% 5px no-repeat,
        linear-gradient(145deg, #f8fafc, #edf1f6);
}

.beta-page-card__fallback > span {
    font-family: 'Outfit', ui-sans-serif, system-ui, sans-serif;
    font-size: 28px;
    font-weight: 650;
    letter-spacing: -0.05em;
}

.beta-page-card__fallback small {
    font-size: 9px;
    font-weight: 800;
}

.beta-page-card__type,
.beta-page-card__visibility {
    position: absolute;
    top: 11px;
    display: inline-flex;
    min-height: 24px;
    align-items: center;
    border-radius: 999px;
    padding: 0 9px;
    font-size: 8px;
    font-weight: 850;
    text-transform: uppercase;
    backdrop-filter: blur(9px);
}

.beta-page-card__type {
    left: 11px;
    background: rgba(255, 255, 255, 0.9);
    color: #536780;
}

.beta-page-card__visibility {
    right: 11px;
    background: rgba(25, 41, 63, 0.82);
    color: #fff;
}

.beta-page-card__body {
    display: grid;
    min-height: 78px;
    grid-template-columns: minmax(0, 1fr);
    align-items: center;
    gap: 14px;
    padding: 15px 76px 15px 44px;
}

.beta-page-card__body strong,
.beta-page-card__body small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.beta-page-card__body strong {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    white-space: normal;
    overflow-wrap: anywhere;
    color: #172d4a;
    font-family: 'Outfit', ui-sans-serif, system-ui, sans-serif;
    font-size: 14px;
    font-weight: 650;
}

.beta-page-card__body small {
    margin-top: 4px;
    color: #8190a5;
    font-size: 9px;
}

.beta-page-card__order {
    position: absolute;
    left: 8px;
    bottom: 23px;
}

.beta-page-card__actions {
    position: absolute;
    display: flex;
    align-items: center;
    gap: 3px;
    right: 10px;
    bottom: 23px;
}

.beta-page-card__settings {
    display: grid;
    width: 28px;
    height: 32px;
    place-items: center;
    border: 0;
    background: transparent;
    color: #71839a;
    transition: color 150ms ease, transform 150ms ease;
}

.beta-page-card__settings :deep(svg) {
    width: 24px;
    height: 24px;
}

.beta-page-card__settings:hover,
.beta-page-card__settings:focus-visible {
    color: var(--main-color);
    transform: scale(1.08);
}

.beta-page-card__settings:focus-visible {
    outline: 2px solid color-mix(in srgb, var(--main-color) 20%, transparent);
    outline-offset: 1px;
}

@media (max-width: 640px) {
    .beta-page-card {
        --beta-page-preview-width: 116px;
        grid-template-columns: var(--beta-page-preview-width) minmax(0, 1fr);
    }

    .beta-page-card__open {
        display: contents;
    }

    .beta-page-card__preview {
        width: var(--beta-page-preview-width);
        height: 112px;
        min-height: 0;
        max-height: 112px;
        align-self: start;
    }

    .beta-page-card__image {
        max-width: 100%;
        max-height: 112px;
    }

    .beta-page-card__type {
        top: 8px;
        left: 8px;
    }

    .beta-page-card__visibility {
        top: auto;
        right: auto;
        bottom: 8px;
        left: 8px;
    }

    .beta-page-card__body {
        min-height: 112px;
        padding: 15px 76px 15px 36px;
    }

    .beta-page-card__order {
        left: calc(var(--beta-page-preview-width) + 4px);
        bottom: 40px;
    }

    .beta-page-card__actions {
        bottom: 40px;
    }
}

@media (max-width: 380px) {
    .beta-page-card {
        --beta-page-preview-width: 76px;
    }
}
</style>
